import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Check, Trash2, X } from 'lucide-react';
import type { InjuryRecordEntry } from '@/types';
import { REGION_FULL } from './bodyRegions';

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'bg-blue-500/15 text-blue-400',
  moderate: 'bg-amber-500/15 text-amber-400',
  severe: 'bg-red-500/15 text-red-400',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'text-red-400',
  improving: 'text-amber-400',
  resolved: 'text-green-400',
};

interface RegionGroup {
  region: string;
  label: string;
  injuries: InjuryRecordEntry[];
  worstStatus: 'active' | 'improving' | 'resolved';
  activeCount: number;
}

function groupByRegion(injuries: InjuryRecordEntry[]): RegionGroup[] {
  const map = new Map<string, InjuryRecordEntry[]>();
  for (const inj of injuries) {
    const list = map.get(inj.body_region) || [];
    list.push(inj);
    map.set(inj.body_region, list);
  }

  const statusPriority: Record<string, number> = { active: 0, improving: 1, resolved: 2 };

  const groups: RegionGroup[] = [];
  for (const [region, regionInjuries] of map) {
    const sorted = [...regionInjuries].sort(
      (a, b) => new Date(b.injury_date).getTime() - new Date(a.injury_date).getTime()
    );
    let worst: 'active' | 'improving' | 'resolved' = 'resolved';
    let activeCount = 0;
    for (const inj of sorted) {
      if (statusPriority[inj.status] < statusPriority[worst]) {
        worst = inj.status;
      }
      if (inj.status === 'active' || inj.status === 'improving') activeCount++;
    }
    groups.push({
      region,
      label: REGION_FULL[region] || region,
      injuries: sorted,
      worstStatus: worst,
      activeCount,
    });
  }

  groups.sort((a, b) => {
    const pa = statusPriority[a.worstStatus];
    const pb = statusPriority[b.worstStatus];
    if (pa !== pb) return pa - pb;
    return a.label.localeCompare(b.label);
  });

  return groups;
}

interface Props {
  injuries: InjuryRecordEntry[];
  loading: boolean;
  selectedRegion?: string | null;
  onStatusChange: (injuryId: number, newStatus: string) => void;
  onDelete: (injuryId: number) => void;
}

const InjuryAccordion: React.FC<Props> = ({
  injuries,
  loading,
  selectedRegion,
  onStatusChange,
  onDelete,
}) => {
  const groups = useMemo(() => groupByRegion(injuries), [injuries]);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const regionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Collapsed by default — initial useState(new Set()) handles this.
  // Component remounts on athlete change, so no reset effect needed.

  useEffect(() => {
    if (!selectedRegion) return;
    setExpandedRegions(prev => {
      const next = new Set(prev);
      next.add(selectedRegion);
      return next;
    });
    setTimeout(() => {
      regionRefs.current[selectedRegion]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }, [selectedRegion]);

  const toggleRegion = (region: string) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  };

  if (loading) return <div className="text-sm cgm-text-secondary">Loading...</div>;
  if (injuries.length === 0) return <div className="text-sm cgm-text-secondary opacity-50">No injury records</div>;

  return (
    <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
      {groups.map(group => {
        const isExpanded = expandedRegions.has(group.region);
        const activeOnly = group.injuries.filter(i => i.status === 'active').length;
        const improvingOnly = group.injuries.filter(i => i.status === 'improving').length;
        const statusLabel = group.activeCount > 0
          ? [activeOnly && `${activeOnly} active`, improvingOnly && `${improvingOnly} improving`].filter(Boolean).join(', ')
          : `${group.injuries.length} resolved`;

        return (
          <div
            key={group.region}
            ref={el => { regionRefs.current[group.region] = el; }}
            className={`border border-[var(--card-border)] ${
              selectedRegion === group.region ? 'border-[var(--accent-color)]' : ''
            }`}
          >
            <button
              onClick={() => toggleRegion(group.region)}
              className="w-full flex justify-between items-center px-2 py-1.5 text-xs cgm-bg-secondary hover:cgm-bg-primary transition-colors"
            >
              <span className={`font-semibold ${group.worstStatus === 'resolved' ? 'cgm-text-secondary' : 'cgm-text-primary'}`}>
                {isExpanded ? '▼' : '▸'} {group.label}
              </span>
              <span className={`text-[10px] ${STATUS_COLORS[group.worstStatus]}`}>
                {statusLabel}
              </span>
            </button>

            {isExpanded && (
              <div className="border-t border-[var(--card-border)]">
                {group.injuries.map(injury => (
                  <div key={injury.id} className="px-2 py-1.5 text-xs border-b border-[var(--card-border)] last:border-b-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <div>
                        <span className="font-semibold cgm-text-primary">
                          {injury.injury_name || group.label}
                        </span>
                        <span className={`ml-2 px-1.5 py-0.5 text-[10px] ${SEVERITY_COLORS[injury.severity]}`}>
                          {injury.severity}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <select
                          value={injury.status}
                          onChange={e => { e.stopPropagation(); onStatusChange(injury.id, e.target.value); }}
                          className={`text-[10px] bg-transparent border-none outline-none cursor-pointer ${STATUS_COLORS[injury.status]}`}
                        >
                          <option value="active">Active</option>
                          <option value="improving">Improving</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        {confirmDeleteId === injury.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={async () => { await onDelete(injury.id); setConfirmDeleteId(null); }} className="text-red-400 hover:text-red-300">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => setConfirmDeleteId(null)} className="cgm-text-secondary hover:cgm-text-primary">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setConfirmDeleteId(injury.id)} className="cgm-text-secondary hover:text-red-400">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="cgm-text-secondary">
                      {injury.injury_date} ({injury.days_since}d)
                    </div>
                    {injury.notes && (
                      <div className="mt-0.5 cgm-text-secondary italic">{injury.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InjuryAccordion;
